import appConfig from 'appConfig';

import { CreateControllerFn, ExperimentsBag } from '@wix/yoshi-flow-editor';
import { createEventHandler, getSettingsValues } from '@wix/tpa-settings';

import { EDITOR_SDK_PUBLIC_DATA_KEY } from '@/constants';

import { Events, WixCodeMembersDummy } from './types';
import settingsParams from './settingsParams';

const RTL_LANGUAGE_CODES = new Set(['ar', 'he', 'fa', 'ur', 'yi']);
const getDirection = (languageCode: string): 'ltr' | 'rtl' =>
  RTL_LANGUAGE_CODES.has(String(languageCode).toLowerCase()) ? 'rtl' : 'ltr';

const createController: CreateControllerFn = async ({
  controllerConfig,
  appData,
  flowAPI,
}) => {
  const { setProps, config, wixCodeApi } = controllerConfig;

  const { withErrorHandler, getResolvedError } = flowAPI.errorHandler;
  const api = flowAPI.controllerConfig.wixCodeApi;

  let member: Awaited<
    ReturnType<NonNullable<WixCodeMembersDummy['currentMember']>['getMember']>
  >;
  const currentMember = (api as any).members
    ?.currentMember as WixCodeMembersDummy['currentMember'];
  if (currentMember === undefined) {
    member = undefined;
  } else {
    member = await withErrorHandler(() => currentMember.getMember(), {
      errorCodesMap: {
        applicationError: {
          DEADLINE_EXCEEDED: false,
          UNAUTHENTICATED: false,
          PERMISSION_DENIED: false,
          NOT_FOUND: false,
        },
      },
    }).catch((error) => {
      throw getResolvedError(error);
    });
  }

  const avatar = member?.profile?.profilePhoto?.url;

  const navigation = {
    baseUrl: api.location.baseUrl,
    navigateTo: api?.location?.navigateTo,
    navigateToUrl: api?.location?.to,
  };

  const componentPublicData = config.publicData.COMPONENT ?? {};
  const appPublicData = config.publicData.APP ?? {};
  const publicDataExperiments = appPublicData[EDITOR_SDK_PUBLIC_DATA_KEY]
    ?.experiments as ExperimentsBag | undefined;
  const handler = createEventHandler<Events>(componentPublicData);
  const onHandlerEvent = handler.on.bind(this);
  const onHandlerReset = handler.onReset.bind(this);

  const initialLayoutOpen =
    componentPublicData.initialVisualLayoutState === 'visible';

  const instanceToken =
    api?.site?.getAppToken && api.site.getAppToken(appConfig.appDefinitionId);

  const siteLanguageDirection = getDirection(wixCodeApi?.site.language ?? '');

  const getSettings = (pd: any) => {
    const { currentLanguage, isEnabled, siteLanguages } =
      wixCodeApi.window.multilingual;

    const currentLanguageSettings = getSettingsValues(pd, settingsParams, {
      t: flowAPI.translations.t,
      language: isEnabled ? currentLanguage : undefined,
    });

    const primaryLanguageSettings = isEnabled
      ? getSettingsValues(pd, settingsParams, {
          t: flowAPI.translations.t,
          language: siteLanguages.find(
            ({ isPrimaryLanguage }) => isPrimaryLanguage,
          )!.languageCode,
        })
      : currentLanguageSettings;

    const settings = {
      current: currentLanguageSettings,
      primary: primaryLanguageSettings,
    };

    return settings;
  };

  api.user.onLogin(async () => {
    const _member = await (
      (api as any).members as WixCodeMembersDummy | undefined
    )?.currentMember?.getMember();

    const _avatar = _member?.profile?.profilePhoto?.url;
    setProps({ avatar: _avatar });
  });

  api.site.onInstanceChanged((e) => {
    // https://github.com/wix-private/wix-ai-assistant-bot/issues/4451
    setProps({
      instanceToken: e.instance,
    });
  }, appConfig.appDefinitionId);

  return {
    async pageReady() {
      setProps({
        onHandlerEvent,
        onHandlerReset,
        navigation,
        initialLayoutOpen,
        instanceToken,
        avatar,
        page: flowAPI.viewerScriptFlowAPI.siteAPI.currentPage,
        settings: getSettings(componentPublicData),
        ecom: (api as any).ecom,
        experiments: publicDataExperiments,
        direction: siteLanguageDirection,
      });
    },
    updateConfig($w, c) {
      const pd = c.publicData.COMPONENT || {};
      handler.notify(pd);
      setProps({
        settings: getSettings(pd),
        direction: siteLanguageDirection,
      });
    },
  };
};

export default createController;
